import { PaywallProps } from '@components/Paywall';
import { MidArticleEventProps } from '@components/pages/Article/MidArticle/Event/interfaces';
import { Env } from '@contexts/AppContext';
import { Author } from '@interfaces/authors';
import {
  Article,
  Media,
  MostPopularArticle,
  ParsedTakeaway,
  ScriptList,
} from '@interfaces/homepage';
import { User } from '@utils/backend-api/currentUser';

export enum ArticleTheme {
  Data = 'data',
  Feature = 'feature',
  Graphic = 'graphic',
  Standard = 'standard',
  Weekend = 'weekend',
}

// TODO: Remove after experiment
// {@link https://app.asana.com/0/1201413338509198/1208029113890850/f}
export enum PaywallVariant {
  FullBleed = 'full_bleed',
  Standard = 'standard',
  TwoOption = 'two_option',
}

export enum MidArticleUnitTypes {
  DeepResearch = 'deep_research',
  Event = 'event',
  Recirc = 'recirc',
}

/**
 * The minimal, stable set of props the server needs to produce an SEO-correct render.
 *
 * Rule: only props that are cache-safe and contain no user-specific state belong here.
 * Everything added here must come from Rails with no knowledge of the current user.
 *
 * Access control is encoded in fullText: non-null means the viewer may read the full
 * article (subscriber or detected bot); null means locked. The client trusts this
 * decision and derives canViewFullArticle as `article.fullText !== null`. It never
 * needs to know why the server made the decision.
 *
 * Intentionally absent:
 * - isSEOBotAndArticleIsNotFree — redundant given the fullText encoding above.
 * - isGdprRequired — geo-based but irrelevant to SEO content. All usages are in
 *   post-hydration UI (email capture, cookie banners). Derive via useGdprRequired()
 *   from a Rails-set cookie; no API call needed.
 */
export interface ArticleSSRProps {
  article: {
    id: number;
    slug: string;
    title: string;
    subTitle?: string;
    highlight: string;
    authors: Author[];
    publishedAt: string;
    picture?: string;
    pictureCaption?: string;
    pictureLink?: string;
    media?: Media;
    isPro: boolean;
    freeBlurb: string;
    fullText: string | null; // null = locked; derive canViewFullArticle as fullText !== null
    takeaway?: ParsedTakeaway[]; // absent when locked
    addendum?: string; // absent when locked
    scriptList?: ScriptList[];
    featuredGraphic?: string; // Graphic layout only
    newsletterSlug?: string; // replaces newsletterId prop
  };
  theme: ArticleTheme; // determines which layout component renders
  env: Env; // needed for imgix image URLs and app-level behaviour
  isWebview: boolean;
}

// Transitional superset of ArticleSSRProps. Fields inherited from ArticleSSRProps and
// ThemeViewsProps are not re-declared here. article is re-declared to narrow fullText
// from string | null (the SSR target) to string (the current Rails reality).
export interface ArticleProps extends ArticleSSRProps, ThemeViewsProps {
  article: Article; // narrows ArticleSSRProps['article'].fullText from string | null to string
  hideAwarenessBar: boolean;
  // TODO Step 3: remove. Article/index.tsx still seeds AppContextProvider with this
  // while useCurrentUser() is the canonical client-side source.
  initialCurrentUser?: User;
  // Still passed from Rails while cookie-based derivation (useGdprRequired) is wired up.
  isGdprRequired: boolean;
  // LockedArticle and Paywall already self-fetch paywall data via useBlueConicPaywallDialogue().
  // This prop is unused from Rails and should be removed when the paywall is restructured.
  paywallProps?: PaywallProps;
}

export interface ThemeViewsProps {
  article: Article;
  audioActive?: boolean;
  canShareArticle: boolean;
  canViewFullArticle: boolean;
  env: Env;
  // The server encodes the bot/subscriber access decision in fullText. Prefer
  // article.fullText !== null as the sole visibility signal over this flag.
  isSEOBotAndArticleIsNotFree: boolean;
  isWebview?: boolean;
  midArticleUnitType?: MidArticleUnitTypes;
  mostPopularArticles: MostPopularArticle[];
  newsletterId?: string | number;
  relatedArticles: Article[];
  relatedEvent?: MidArticleEventProps;
}
